import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertTriangle } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

import { FormularioCampos, type ValoresCampos } from "./FormularioCampos"
import { HojaDocumentoTH } from "./HojaDocumentoTH"
import { CATEGORIAS, type DocumentoTH, type PlantillaTH, type VistaPrevia } from "./tipos"

type Persona = { id: number; nombres: string; apellidos: string; documento: string }

/**
 * Generar un documento: elegir la plantilla (y el empleado, si lo genera Talento
 * Humano), llenar lo que pide, VER cómo queda y emitirlo.
 *
 * ⚠ **Dos modos, una pantalla.** En `autoservicio` el empleado pide con SUS datos: no
 * elige persona —la pone el servidor— y sólo ve las plantillas de autoservicio. En `th`
 * el encargado elige a quién.
 *
 * ⚠ **La vista previa la compone el SERVIDOR**, con la ficha de verdad: componerla aquí
 * sería una segunda implementación de las variables. Y dice qué datos FALTAN en la
 * ficha, que es lo que hay que corregir antes de emitir un documento legal.
 */
export function GenerarDocumento({
  modo,
  abierto,
  onCerrar,
  onGenerado,
}: {
  modo: "th" | "autoservicio"
  abierto: boolean
  onCerrar: () => void
  onGenerado: (doc: DocumentoTH) => void
}) {
  const clienteQuery = useQueryClient()
  const base = modo === "th" ? "documentos-th" : "mis-documentos"
  const [plantillaId, setPlantillaId] = useState("")
  const [personaId, setPersonaId] = useState("")
  const [valores, setValores] = useState<ValoresCampos>({})
  const [previa, setPrevia] = useState<VistaPrevia | null>(null)

  const plantillas = useQuery({
    queryKey: ["plantillas-th", modo],
    queryFn: () =>
      api.get<PlantillaTH[]>(
        modo === "th"
          ? "/api/v1/plantillas-th?solo_activas=true"
          : "/api/v1/mis-documentos/plantillas",
      ),
    enabled: abierto,
  })
  const personas = useQuery({
    queryKey: ["personas", "empleados-activos"],
    queryFn: () =>
      api.get<{ items: Persona[] }>("/api/v1/personas?personal=true&estado=ACTIVO&limit=200"),
    enabled: abierto && modo === "th",
  })

  const plantilla = plantillas.data?.find((p) => String(p.id) === plantillaId)
  const cuerpo = () => ({
    plantilla_id: Number(plantillaId),
    persona_id: modo === "th" ? Number(personaId) : null,
    campos: valores,
  })

  const verPrevia = useMutation({
    mutationFn: () => api.post<VistaPrevia>(`/api/v1/${base}/vista-previa`, cuerpo()),
    onSuccess: setPrevia,
  })
  const generar = useMutation({
    mutationFn: () => api.post<DocumentoTH>(`/api/v1/${base}`, cuerpo()),
    onSuccess: (doc) => {
      clienteQuery.invalidateQueries({ queryKey: ["documentos-th"] })
      clienteQuery.invalidateQueries({ queryKey: ["mis-documentos"] })
      onGenerado(doc)
    },
    meta: { exito: modo === "th" ? "Documento emitido" : "Solicitud enviada" },
  })

  const listo = plantillaId !== "" && (modo === "autoservicio" || personaId !== "")
  const cambiar = (f: () => void) => {
    f()
    setPrevia(null)
  }
  const error = verPrevia.error ?? generar.error

  return (
    <Modal
      abierto={abierto}
      titulo={modo === "th" ? "Generar un documento" : "Nueva solicitud"}
      descripcion={
        modo === "th"
          ? "Se llena solo con la ficha del empleado; aquí se completa lo demás."
          : "Va con tus datos de la ficha; aquí completas lo que falta."
      }
      ancho="lg"
      onCerrar={onCerrar}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className={modo === "th" ? "" : "sm:col-span-2"}>
          <label htmlFor="gen-plantilla" className="mb-1 block text-xs font-medium uppercase">
            Documento
          </label>
          <select
            id="gen-plantilla"
            className="campo"
            value={plantillaId}
            onChange={(e) =>
              cambiar(() => {
                setPlantillaId(e.target.value)
                setValores({})
              })
            }
          >
            <option value="">Elegir…</option>
            {CATEGORIAS.map((cat) => {
              const delGrupo = (plantillas.data ?? []).filter((p) => p.categoria === cat.valor)
              return delGrupo.length === 0 ? null : (
                <optgroup key={cat.valor} label={cat.etiqueta}>
                  {delGrupo.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </optgroup>
              )
            })}
          </select>
        </div>
        {modo === "th" && (
          <div>
            <label htmlFor="gen-persona" className="mb-1 block text-xs font-medium uppercase">
              Empleado
            </label>
            <select
              id="gen-persona"
              className="campo"
              value={personaId}
              onChange={(e) => cambiar(() => setPersonaId(e.target.value))}
            >
              <option value="">Elegir…</option>
              {(personas.data?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombres} {p.apellidos}
                </option>
              ))}
            </select>
            {personas.isError && (
              <p className="mt-1 text-xs" style={{ color: "var(--destructive)" }}>
                No se pudo leer el personal: hace falta poder ver las fichas.
              </p>
            )}
          </div>
        )}
      </div>

      {plantilla && (
        <div className="mt-4">
          {plantilla.descripcion && (
            <p className="mb-3 text-sm" style={{ color: "var(--fg-muted)" }}>
              {plantilla.descripcion}
            </p>
          )}
          <FormularioCampos
            campos={plantilla.campos}
            valores={valores}
            onCambiar={(v) => cambiar(() => setValores(v))}
          />
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
          {error instanceof ErrorApi ? error.message : "No se pudo preparar el documento"}
        </p>
      )}

      {previa && (
        <div className="mt-4">
          {previa.faltantes.length > 0 && (
            <p
              className="mb-3 flex items-start gap-2 text-sm"
              style={{ color: "var(--warning)" }}
              data-testid="faltantes"
            >
              <AlertTriangle size={16} aria-hidden={true} className="mt-0.5 shrink-0" />
              La ficha no tiene: {previa.faltantes.join(", ")}. Saldrán escritos entre llaves;
              conviene completarlos antes de emitir.
            </p>
          )}
          <section
            className="superficie max-h-[50vh] overflow-y-auto p-4"
            aria-label="Vista previa"
          >
            <HojaDocumentoTH titulo={plantilla?.nombre ?? ""} cuerpo={previa.cuerpo} />
          </section>
        </div>
      )}

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Boton variante="fantasma" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton disabled={!listo || verPrevia.isPending} onClick={() => verPrevia.mutate()}>
          Ver cómo queda
        </Boton>
        <Boton
          variante="primario"
          disabled={!listo || previa === null || generar.isPending}
          onClick={() => generar.mutate()}
        >
          {modo === "th"
            ? "Emitir"
            : plantilla?.requiere_aprobacion
              ? "Enviar a Talento Humano"
              : "Generar"}
        </Boton>
      </div>
    </Modal>
  )
}
